import type {
  RequirementLevel,
  RunFeedPreviewColumn,
  RunFeedPreviewVerdictItem,
  RunFeedPreviewDrop,
  RunFeedPreviewRow,
  RunItemVerdictReason,
  RunSetAsideItem,
  RunSkuPreviewAttribute,
  RunSkuPreviewResponse,
} from "@nova-ai/shared"
import { displayVerdictReasons } from "@nova-ai/shared"
import { Link } from "@tanstack/react-router"
import {
  Archive,
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Wrench,
  X,
} from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { cn } from "../../lib/utils"
import { formatDateTime } from "../../lib/format-date"
import { useRunSkuPreview } from "../../services/use-runs"
import {
  OFF_CATEGORY_HINT,
  OffCategoryBadge,
  REQUIREMENT_STYLES,
  RequirementBadge,
} from "../marketplaces/requirement-badge"
import { SourceBadge } from "./source-badge"
import {
  buildRequirementCoverage,
  buildRowlessInspection,
  REQUIREMENT_ORDER,
  type RequirementCoverageGroup,
} from "./run-feed-row-drawer.derive"
import { reasonLabel } from "./run-verdict-tabs"
import { SetAsideNoteEditor } from "./set-aside-note-editor"
import { StatusBadge } from "../ui/status-badge"
import { Callout } from "../ui/callout"
import { Table, type TableColumn } from "../ui/table"
import { RichTextValue } from "../ui/rich-text-value"

type RunFeedRowDrawerProps = {
  slug: string
  isHistorical: boolean
  row: RunFeedPreviewRow | null
  columns: readonly RunFeedPreviewColumn[]
  variantRows: readonly RunFeedPreviewRow[]
  previousRow: RunFeedPreviewRow | null
  nextRow: RunFeedPreviewRow | null
  setAsideInfo?: RunSetAsideItem | undefined
  /** The config's inspected verdict reasons; empty when no reasons are available.
   *  Drives the "why this product is NOK" section so NOK products explain
   *  themselves in the same drawer the OK tab uses. */
  verdictReasons?: readonly RunItemVerdictReason[]
  verdictItem?: RunFeedPreviewVerdictItem | undefined
  dropped?: readonly RunFeedPreviewDrop[] | undefined
  onClose: () => void
  onNavigateRow: (row: RunFeedPreviewRow) => void
}

function useInspectionEnrichment(
  slug: string,
  row: RunFeedPreviewRow | null,
  isHistorical: boolean
) {
  const preview = useRunSkuPreview(slug, isHistorical ? "" : (row?.configSku ?? ""))
  return isHistorical ? undefined : preview.data
}

function HistoricalDrawerNotice({ isHistorical }: { isHistorical: boolean }) {
  if (!isHistorical) {
    return null
  }
  return (
    <Callout tone="warning" dense>
      Historical inspection — not valid for shipment. Only persisted feed values are shown.
    </Callout>
  )
}

function RowlessDrawerNotice({
  isPlaceholder,
  isHistorical,
}: {
  isPlaceholder: boolean | undefined
  isHistorical: boolean
}) {
  if (!isPlaceholder || isHistorical) {
    return null
  }
  return (
    <Callout tone="info" dense>
      Product information for review. This product has no rows in the feed. Variant values are shown
      separately below.
    </Callout>
  )
}

export function RunFeedRowDrawer({
  slug,
  isHistorical,
  row: feedRow,
  columns: feedColumns,
  variantRows,
  previousRow,
  nextRow,
  setAsideInfo,
  verdictReasons,
  verdictItem,
  dropped,
  onClose,
  onNavigateRow,
}: RunFeedRowDrawerProps) {
  // Provenance (source, rewrites, gaps) comes from the per-SKU preview, which
  // already resolves writer declarations + per-SKU AI/human overrides. The feed
  // preview only carries final cell values, so we join the two here. The query
  // is disabled until a row opens and is cached per configSku, so paging
  // between variants of the same parent is instant.
  const enrichment = useInspectionEnrichment(slug, feedRow, isHistorical)
  const attributes = enrichment?.attributes
  const { row, columns, variantAttributes } = buildRowlessInspection(
    feedRow,
    feedColumns,
    attributes,
    isHistorical
  )
  const attributesByCode = buildAttributeMap(attributes, row?.simpleSku ?? null)
  // Per-attribute requirement level (required / recommended / …) for the SKU's
  // whole category now rides on the same per-SKU preview — the server already
  // has the metadata cache loaded for this request, so it computes the map
  // there instead of making the drawer take a second round-trip. Keyed by
  // attrCode; undefined until the preview resolves.
  const requirementByCode = enrichment?.requirementByCode
  // The marketplace's copy roles (title / short / long description) resolve to
  // attribute codes here so the hero can present them as a product page rather
  // than three rows in a flat list. Null until the preview resolves, or when the
  // marketplace declares no copy block — the hero then degrades gracefully.
  const hero = buildHeroFields(enrichment?.copy ?? null, row, attributesByCode)
  // Brand + category also live in the product header, so resolve them once and
  // reuse for both the header and the exclusion decision below.
  const brand = heroBrand(enrichment, columns, row)
  const category = heroCategory(enrichment, columns, row)
  // The hero copy fields (title / short / long) plus the brand + category rows it
  // already shows are dropped from the attribute lists below, so nothing renders
  // twice.
  const heroCodes = collectHeroCodes(hero, brand, category, enrichment)
  // Remaining AI-authored fields (AI overrides, composition, …) bubble to the top
  // so a reviewer can verify them without scrolling past the deterministic
  // fact/copy/lookup columns; those are then grouped by requirement level
  // (required first, off-category "n/a" last) once the per-SKU preview resolves.
  const sections = groupColumnsBySection(columns, attributesByCode, heroCodes, requirementByCode)
  // Showcase the images that actually ship in this row (backdrop-selected,
  // deduped, in feed-column order) rather than the raw Magento gallery — so
  // the preview at the top matches the main_image / image_N cells below.
  const shippedImages = row ? extractShippedImages(row, columns) : []

  useEffect(() => {
    if (!row) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [onClose, row])

  if (!row) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <button
        aria-label="Close feed row preview"
        className="motion-drawer-backdrop absolute inset-0 bg-foreground/20 backdrop-blur-[1px]"
        onClick={onClose}
        type="button"
      />

      <aside className="motion-drawer-panel relative h-full w-full max-w-3xl overflow-y-auto border-l border-border bg-background shadow-xl">
        <DrawerToolbar
          previousRow={previousRow}
          nextRow={nextRow}
          onClose={onClose}
          onNavigateRow={onNavigateRow}
        />

        <div className="flex flex-col gap-6 px-5 py-5">
          <HistoricalDrawerNotice isHistorical={isHistorical} />

          <RowlessDrawerNotice isPlaceholder={row.isPlaceholder} isHistorical={isHistorical} />

          <ProductHero
            key={row.simpleSku ?? row.configSku}
            configSku={row.configSku}
            simpleSku={row.simpleSku}
            hero={hero}
            brand={brand}
            category={category}
            images={shippedImages}
            columns={columns}
            requirementByCode={row.isPlaceholder ? undefined : requirementByCode}
            values={row.values}
          />

          {setAsideInfo && <SetAsideNotice slug={slug} item={setAsideInfo} />}

          <InspectionEvidence item={verdictItem} dropped={dropped ?? []} />

          <VerdictReasonSection reasons={verdictReasons} />

          <VariantTable
            simples={enrichment?.magento.simples ?? []}
            selectedSimpleSku={row.simpleSku}
            variantRows={variantRows}
            onNavigateRow={onNavigateRow}
          />

          <DrawerSections
            sections={sections}
            row={row}
            attributesByCode={attributesByCode}
            requirementByCode={requirementByCode}
            variantAttributes={variantAttributes}
          />
        </div>
      </aside>
    </div>
  )
}

function InspectionEvidence({
  item,
  dropped,
}: {
  item: RunFeedPreviewVerdictItem | undefined
  dropped: readonly RunFeedPreviewDrop[]
}) {
  if (!item) {
    return null
  }
  const droppedCount = item.droppedCount ?? dropped.length
  return (
    <section className="space-y-2 text-sm" aria-label="Product inspection evidence">
      {item.setAside && (
        <p>
          <strong>Set aside:</strong>

          {item.setAsideReason ?? "No reason recorded"}
        </p>
      )}

      {item.feedRowCount === 0 && <p>No emitted feed rows for this product.</p>}

      {item.evidence && (
        <p className="text-muted-foreground">
          {`Saved validation: ${item.evidence.savedVerdict?.toUpperCase() ?? "not classified"}. ${
            item.evidence.savedAt
              ? `Recorded ${formatDateTime(item.evidence.savedAt)}.`
              : "Recording time unavailable."
          } Historical record; current inspection is shown separately.`}
        </p>
      )}

      {item.evidence?.savedReasons.length ? (
        <ul className="list-disc pl-5 text-muted-foreground">
          {item.evidence.savedReasons.map((reason) => (
            <li key={`${reason.kind}:${reason.reason}`}>{reasonLabel(reason.reason)}</li>
          ))}
        </ul>
      ) : null}

      {droppedCount > 0 && (
        <div>
          <p className="font-medium">Dropped variants: {droppedCount}</p>

          <ul className="list-disc pl-5">
            {dropped.map((drop, index) => (
              <li key={`${drop.simpleSku}:${drop.reason}:${index}`}>
                <span className="font-mono">{drop.simpleSku}</span>

                {": "}

                {reasonLabel(drop.reason)}

                {drop.detail ? ` (${drop.detail})` : ""}
              </li>
            ))}
          </ul>

          {droppedCount > dropped.length && (
            <p className="text-muted-foreground">
              {`Showing ${dropped.length} of ${droppedCount} dropped variants. Evidence sample is capped. Search this product’s SKU to inspect its drops separately.`}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

// The per-run set-aside reason (who parked it this run, and why) plus the
// DURABLE per-product note editor (issue #122). The reason above dies with the
// run; the note below is keyed on the product and survives re-runs — the editor
// is self-contained so it can move to the #121 "Set aside" cohort surface later.
function SetAsideNotice({ slug, item }: { slug: string; item: RunSetAsideItem }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-xs font-medium text-muted-foreground">Set-aside reason (this run)</h3>

        <div className="flex flex-col gap-1.5 rounded-md border border-border/60 px-3 py-2">
          <StatusBadge tone="muted" size="sm" className="w-fit">
            <Archive className="size-3 shrink-0" />
            Set aside
          </StatusBadge>

          <p className="text-sm text-foreground">{item.reason || "No reason recorded."}</p>

          <p className="text-xs text-muted-foreground">
            {`${shortActor(item.setAsideBy)} · ${formatDateTime(item.setAsideAt)}`}
          </p>
        </div>
      </div>

      <SetAsideNoteEditor slug={slug} configSku={item.configSku} note={item.note} />
    </section>
  )
}

// Concise human guidance per fixable gap reason. Keys are the machine reason
// codes from the verdict; only fixable reasons need a line (won't-list reasons
// explain themselves via the chip).
const FIX_GUIDANCE: Record<string, string> = {
  no_category: "Assign a marketplace category, then re-validate.",
  no_writer:
    "Add this category to its canonical writer, deploy, build a candidate, then activate it.",
  missing_required: "Fill the missing required attributes — see the attribute list below.",
  not_in_catalog_mirror: "Run product sync to import this SKU into the catalog mirror.",
  not_tracked: "Register this SKU for the marketplace.",
  no_facts: "No buildable variants — check the source product.",
  no_catalog_row: "No catalog data — run product sync.",
  variant_title_duplicate:
    "This variant's final title is identical to a sibling's, so the marketplace sees duplicate product sheets. Regenerating copy will not help — one title is written per product. Give the siblings a distinguishing value, or re-map the config to a category that declares the axis that actually varies.",
  variant_differentiator_missing:
    "This variant carries no flavour or pack size, so nothing tells it apart from its siblings. Fill one on the catalog-mirror row, or re-map the config to a category that declares one. The feed still builds and ships.",
}

/** The config's verdict reasons, split into fixable (gap) vs won't-list
 *  (eligibility) — the same triage the reason chips and retired NOK drawer used.
 *  Renders nothing for an OK / unclassified config, so the OK tab's drawer is
 *  unchanged. */
function VerdictReasonSection({
  reasons,
}: {
  reasons: readonly RunItemVerdictReason[] | undefined
}) {
  // Symptom hidden, fixable first — same rule the list and chips use.
  const shown = displayVerdictReasons(reasons ?? [])
  if (shown.length === 0) {
    return null
  }
  const fixable = shown.filter((reason) => reason.fixable)
  const blocked = shown.filter((reason) => !reason.fixable)
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-xs font-medium text-muted-foreground">Why this product is NOK</h3>

      <ReasonGroup title="Fixable in Nova" tone="warning" reasons={fixable} />

      <ReasonGroup title="Won't list" tone="destructive" reasons={blocked} />
    </section>
  )
}

/** One triage bucket of verdict reasons, each with its one-line remediation
 *  where one exists. Renders nothing when the bucket is empty. */
function ReasonGroup({
  title,
  tone,
  reasons,
}: {
  title: string
  tone: "warning" | "destructive"
  reasons: RunItemVerdictReason[]
}) {
  if (reasons.length === 0) {
    return null
  }
  const Icon = tone === "warning" ? Wrench : Ban
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{title}</span>

      <ul className="flex flex-col gap-1.5">
        {reasons.map((reason) => {
          const guidance = reason.fixable ? FIX_GUIDANCE[reason.reason] : undefined
          return (
            <li key={`${reason.kind}:${reason.reason}`} className="flex flex-col gap-0.5">
              <StatusBadge tone={tone} size="sm" className="w-fit">
                <Icon className="size-3 shrink-0" />

                {reasonLabel(reason.reason)}
              </StatusBadge>

              {guidance && (
                <span className="ml-0.5 flex gap-1.5 text-xs text-muted-foreground">
                  <span className="select-none text-muted-foreground/50">→</span>

                  {guidance}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type Variant = RunSkuPreviewResponse["magento"]["simples"][number]

function VariantTable({
  simples,
  selectedSimpleSku,
  variantRows,
  onNavigateRow,
}: {
  simples: readonly Variant[]
  selectedSimpleSku: string
  variantRows: readonly RunFeedPreviewRow[]
  onNavigateRow: (row: RunFeedPreviewRow) => void
}) {
  if (simples.length === 0) {
    return null
  }
  const rowsBySimpleSku = new Map(variantRows.map((row) => [row.simpleSku, row]))
  const columns: TableColumn<Variant>[] = [
    {
      key: "sku",
      header: "SKU",
      render: (variant) => {
        const targetRow = rowsBySimpleSku.get(variant.sku)
        return (
          <span
            className={cn(
              "font-mono",
              targetRow ? "text-foreground" : "text-muted-foreground",
              variant.sku === selectedSimpleSku && "font-semibold text-foreground"
            )}
          >
            {variant.sku}
          </span>
        )
      },
    },
    {
      key: "variant",
      header: "Variant",
      render: (variant) => (
        <span className="text-muted-foreground">{variantLabel(variant) || "—"}</span>
      ),
    },
    {
      key: "ean",
      header: "EAN",
      render: (variant) => (
        <span className={cn("font-mono", !variant.ean && "text-destructive")}>
          {variant.ean ?? "missing"}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price",
      align: "right",
      render: (variant) => {
        // `products.price`, never `price_final` (issue #336) — this column must
        // show what the offer feed uploads. A live Magento promo rides along as
        // a muted annotation so the discount stays visible without implying the
        // marketplace sees it.
        const { price, priceFinal } = variant
        const promo = priceFinal != null && priceFinal !== price ? priceFinal : null
        return (
          <span className={cn("tabular-nums", price == null && "text-destructive")}>
            {price == null ? "no price" : price.toLocaleString()}

            {promo != null && (
              <span className="text-muted-foreground ml-1 text-xs">
                promo {promo.toLocaleString()}
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: "stock",
      header: "Stock",
      render: (variant) => (
        <span className="text-muted-foreground">{variant.stockStatus ?? "—"}</span>
      ),
    },
    {
      key: "state",
      header: "State",
      render: (variant) => <VariantState delisted={variant.isDelisted} enabled={variant.enabled} />,
    },
  ]
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-medium text-muted-foreground">
        {`Variants (${simples.length})`}
      </h3>

      <Table
        columns={columns}
        rows={simples}
        rowKey={(variant) => variant.sku}
        cellAlign="middle"
        dense
        isRowInteractive={(variant) => rowsBySimpleSku.has(variant.sku)}
        onRowClick={(variant) => {
          const targetRow = rowsBySimpleSku.get(variant.sku)
          if (targetRow) {
            onNavigateRow(targetRow)
          }
        }}
        rowClassName={(variant) =>
          cn(
            variant.sku === selectedSimpleSku && "bg-muted hover:bg-muted",
            !rowsBySimpleSku.has(variant.sku) && "opacity-60"
          )
        }
      />
    </section>
  )
}

function variantLabel(variant: Variant): string {
  return [variant.size, variant.color, variant.flavor].filter(Boolean).join(" · ")
}

function VariantState({ delisted, enabled }: { delisted: boolean; enabled: boolean }) {
  if (delisted) {
    return <span className="text-destructive">delisted</span>
  }
  if (!enabled) {
    return <span className="text-muted-foreground">disabled</span>
  }
  return <span className="text-success">active</span>
}

function shortActor(raw: string): string {
  const withoutProvider = raw.replace(/^[a-z0-9]+:/i, "")
  const localPart = withoutProvider.split("@")[0]
  return localPart === undefined || localPart === "" ? withoutProvider : localPart
}

/** Index a SKU preview's attributes by attrCode for this specific feed row.
 *  Parent-scoped attrs apply to every variant row; variant-scoped attrs only
 *  apply to their own simple SKU. When both share a code, the variant entry
 *  wins because it's the value that actually ships in this row. */
function buildAttributeMap(
  attributes: RunSkuPreviewAttribute[] | undefined,
  simpleSku: string | null
): Map<string, RunSkuPreviewAttribute> {
  const map = new Map<string, RunSkuPreviewAttribute>()
  if (!attributes) {
    return map
  }
  for (const attr of attributes) {
    const appliesToRow = attr.scope === "config" || attr.simpleSku === simpleSku
    if (!appliesToRow) {
      continue
    }
    const existing = map.get(attr.attrCode)
    if (!existing || attr.scope === "simple") {
      map.set(attr.attrCode, attr)
    }
  }
  return map
}

const AI_SOURCES = new Set<RunSkuPreviewAttribute["source"]>(["ai_generate", "ai_override"])

/** A feed cell counts as an image for the showcase strip when its value is an
 *  image URL. Marketplace-agnostic — no dependency on attr-code naming. */
const IMAGE_URL_RE = /^https?:\/\/.+\.(jpe?g|png|webp|gif|avif)(\?.*)?$/i

/** One resolved hero copy field: the feed cell value plus its provenance, so the
 *  product header can render the value and still show a source badge + char
 *  budget. `attribute` is undefined while the per-SKU preview loads. */
type HeroField = {
  code: string
  value: string
  attribute: RunSkuPreviewAttribute | undefined
}

type HeroFields = {
  title: HeroField | null
  shortDescription: HeroField | null
  longDescription: HeroField | null
}

/** Resolve the marketplace's copy roles to this row's feed values + provenance.
 *  Each role is present only when the marketplace maps it to an attribute code;
 *  the hero degrades to whatever is available (a title-only header is fine). */
function buildHeroFields(
  copy: { title: string; shortDescription: string; longDescription: string } | null,
  row: RunFeedPreviewRow | null,
  attributesByCode: Map<string, RunSkuPreviewAttribute>
): HeroFields {
  const resolve = (code: string | undefined): HeroField | null => {
    if (!code || !row) {
      return null
    }
    return { code, value: row.values[code] ?? "", attribute: attributesByCode.get(code) }
  }
  return {
    title: resolve(copy?.title),
    shortDescription: resolve(copy?.shortDescription),
    longDescription: resolve(copy?.longDescription),
  }
}

/** The images that actually ship in this row: feed cells whose value is an image
 *  URL, deduped and kept in feed-column order so the carousel matches the
 *  main_image / image_N cells exactly. */
function extractShippedImages(
  row: RunFeedPreviewRow,
  columns: readonly RunFeedPreviewColumn[]
): { url: string }[] {
  const seen = new Set<string>()
  const out: { url: string }[] = []
  for (const column of columns) {
    const value = row.values[column.code]
    if (value && IMAGE_URL_RE.test(value) && !seen.has(value)) {
      seen.add(value)
      out.push({ url: value })
    }
  }
  return out
}

/** Brand for the product header — the value that ships in the feed (e.g.
 *  "GYMBEAM"), read from the brand feed column. NOT the Magento source brand,
 *  which a lookup/transform may swap before it reaches the feed. Null when the
 *  marketplace declares no brand column or the cell is empty. */
function heroBrand(
  data: RunSkuPreviewResponse | undefined,
  columns: readonly RunFeedPreviewColumn[],
  row: RunFeedPreviewRow | null
): string | null {
  const code = data?.brandAttrCode ?? undefined
  const column = code ? columns.find((c) => c.code === code) : undefined
  return feedCellLabel(code, column, row)
}

/** Category for the product header — the marketplace category that ships in the
 *  feed (its human label when the column carries one), read from the category
 *  feed column. NOT the Magento breadcrumb. Null when there's no category cell. */
function heroCategory(
  data: RunSkuPreviewResponse | undefined,
  columns: readonly RunFeedPreviewColumn[],
  row: RunFeedPreviewRow | null
): string | null {
  const code = data?.categoryAttrCode ?? undefined
  const column = code ? columns.find((c) => c.code === code) : undefined
  return feedCellLabel(code, column, row)
}

/** Resolve a feed cell to its display string: the column's human label for the
 *  value when present, else the raw value. Empty/missing → null. */
function feedCellLabel(
  code: string | null | undefined,
  column: RunFeedPreviewColumn | undefined,
  row: RunFeedPreviewRow | null
): string | null {
  if (!code || !row) {
    return null
  }
  const value = row.values[code]?.trim()
  if (!value) {
    return null
  }
  return column?.valueLabels?.[value] ?? value
}

/** Codes shown in the hero, so the attribute lists below can skip them: the copy
 *  fields always, plus the brand / category feed columns when the header actually
 *  renders those (guarding against hiding the only source of a value). */
function collectHeroCodes(
  hero: HeroFields,
  brand: string | null,
  category: string | null,
  data: RunSkuPreviewResponse | undefined
): Set<string> {
  const codes = new Set<string>()
  for (const field of [hero.title, hero.shortDescription, hero.longDescription]) {
    if (field) {
      codes.add(field.code)
    }
  }
  if (brand && data?.brandAttrCode) {
    codes.add(data.brandAttrCode)
  }
  if (category && data?.categoryAttrCode) {
    codes.add(data.categoryAttrCode)
  }
  return codes
}

/** Count colour: complete reads neutral; an incomplete REQUIRED group is the
 *  only one worth alarming on (red), other incomplete groups stay quiet. */
function summaryCountColor(group: RequirementCoverageGroup): string {
  if (group.filled >= group.total) {
    return "text-foreground"
  }
  return group.level === "required" ? "text-destructive" : "text-muted-foreground"
}

/** Coverage summary at the top of the drawer: "Required 10/10 · Recommended
 *  8/10 · Optional 10/20" — a scannable read on how complete this row is against
 *  its category before scrolling the attribute list. Incomplete groups expand to
 *  the exact empty attribute codes counted by the denominator. */
function RequirementCoverage({
  requirementByCode,
  values,
  columns,
}: {
  requirementByCode: Record<string, RequirementLevel> | undefined
  values: Record<string, string>
  columns: readonly RunFeedPreviewColumn[]
}) {
  const groups = buildRequirementCoverage(requirementByCode, values, columns)
  const [expandedLevel, setExpandedLevel] = useState<RequirementLevel | null>(null)
  const expandedGroup =
    groups.find((group) => group.level === expandedLevel && group.missing.length > 0) ?? null
  if (groups.length === 0) {
    return null
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 rounded-md border border-border bg-muted/20 px-3 py-2">
        {groups.map((group) => (
          <RequirementCoveragePill
            key={group.level}
            group={group}
            isExpanded={expandedLevel === group.level}
            onToggle={() => {
              setExpandedLevel((current) => (current === group.level ? null : group.level))
            }}
          />
        ))}
      </div>

      {expandedGroup && <MissingRequirementPanel group={expandedGroup} />}
    </div>
  )
}

function RequirementCoveragePill({
  group,
  isExpanded,
  onToggle,
}: {
  group: RequirementCoverageGroup
  isExpanded: boolean
  onToggle: () => void
}) {
  const content = (
    <>
      <span className={cn("size-1.5 rounded-full", REQUIREMENT_STYLES[group.level].dot)} />

      <span className="text-muted-foreground">{REQUIREMENT_STYLES[group.level].label}</span>

      <span className={cn("font-medium tabular-nums", summaryCountColor(group))}>
        {`${group.filled}/${group.total}`}
      </span>
    </>
  )

  if (group.missing.length === 0 || group.level === "not_needed") {
    return <div className="flex items-center gap-1.5 text-xs">{content}</div>
  }

  const Icon = isExpanded ? ChevronDown : ChevronRight
  return (
    <button
      type="button"
      className="flex items-center gap-1.5 rounded-full text-xs transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-expanded={isExpanded}
      aria-label={`${isExpanded ? "Hide" : "Show"} ${group.missing.length} empty ${REQUIREMENT_STYLES[group.level].label.toLowerCase()} attributes`}
      onClick={onToggle}
    >
      {content}

      <Icon className="size-3 text-muted-foreground/60" />
    </button>
  )
}

function MissingRequirementPanel({ group }: { group: RequirementCoverageGroup }) {
  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-card">
      <div className="flex items-center justify-between border-b border-border/50 px-3 py-2 text-xs">
        <span className="font-medium text-foreground">
          {`${REQUIREMENT_STYLES[group.level].label} empty`}
        </span>

        <span className="tabular-nums text-muted-foreground">{group.missing.length}</span>
      </div>

      <div className="max-h-72 overflow-auto divide-y divide-border/40">
        {group.missing.map((item) => (
          <div
            key={item.code}
            className="grid gap-x-3 gap-y-0.5 px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_auto]"
          >
            <div className="min-w-0">
              <div className="break-words text-foreground">{item.label}</div>

              <div className="break-all font-mono text-muted-foreground/70">{item.code}</div>
            </div>

            <span className="text-muted-foreground/50">empty</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** A drawer section's requirement grouping: one of the category's levels, or
 *  "off_category" for columns owned by sibling categories in the run ("n/a"). */
type FeedSectionLevel = RequirementLevel | "off_category"

type FeedSectionData = {
  id: string
  title: string
  /** Set when the section groups one requirement level; undefined for the AI
   *  section and the flat fallback while the schema loads. */
  level?: FeedSectionLevel
  columns: RunFeedPreviewColumn[]
}

/** Partition feed columns into ordered sections: AI-authored ones (copy + AI
 *  overrides) first under "Generated copy", then the rest grouped by the
 *  category's requirement level — required → recommended → optional — with
 *  off-category ("n/a") columns last, so the coverage that matters is visible
 *  before the noise of sibling-category columns. Feed order is preserved
 *  within each group. Until the per-SKU preview resolves the schema the rest
 *  stays one flat "Feed attributes" list rather than flashing a wrong
 *  grouping. Columns surfaced in the product hero (title / short / long
 *  description) are skipped so they aren't shown twice. The caller skips a
 *  section once it has no columns. */
function groupColumnsBySection(
  columns: readonly RunFeedPreviewColumn[],
  attributesByCode: Map<string, RunSkuPreviewAttribute>,
  excludeCodes: ReadonlySet<string>,
  requirementByCode: Record<string, RequirementLevel> | undefined
): FeedSectionData[] {
  const ai: RunFeedPreviewColumn[] = []
  const rest: RunFeedPreviewColumn[] = []
  for (const column of columns) {
    if (excludeCodes.has(column.code)) {
      continue
    }
    const source = attributesByCode.get(column.code)?.source
    if (source && AI_SOURCES.has(source)) {
      ai.push(column)
    } else {
      rest.push(column)
    }
  }
  const sections: FeedSectionData[] = [{ id: "ai", title: "Generated copy", columns: ai }]
  if (!requirementByCode) {
    sections.push({ id: "rest", title: "Feed attributes", columns: rest })
    return sections
  }
  const byLevel = new Map<FeedSectionLevel, RunFeedPreviewColumn[]>()
  for (const column of rest) {
    const level: FeedSectionLevel = requirementByCode[column.code] ?? "off_category"
    const group = byLevel.get(level) ?? []
    group.push(column)
    byLevel.set(level, group)
  }
  for (const level of [...REQUIREMENT_ORDER, "off_category" as const]) {
    const grouped = byLevel.get(level)
    if (grouped) {
      sections.push({ id: level, title: sectionTitle(level), level, columns: grouped })
    }
  }
  return sections
}

function sectionTitle(level: FeedSectionLevel): string {
  return level === "off_category" ? "Not applicable" : REQUIREMENT_STYLES[level].label
}

/** Header dot for a requirement section — the same colour language as the
 *  requirement chips; off-category gets the outlined "n/a" dot. */
function sectionDotClass(level: FeedSectionLevel): string {
  return level === "off_category"
    ? "border border-muted-foreground/50"
    : REQUIREMENT_STYLES[level].dot
}

/** The deterministic/attribute review lists below the product hero: each
 *  non-empty section as a quiet labelled stack of attribute rows. */
function DrawerSections({
  sections,
  row,
  attributesByCode,
  requirementByCode,
  variantAttributes,
}: {
  sections: FeedSectionData[]
  row: RunFeedPreviewRow
  attributesByCode: Map<string, RunSkuPreviewAttribute>
  requirementByCode: Record<string, RequirementLevel> | undefined
  variantAttributes: ReadonlyMap<string, readonly RunSkuPreviewAttribute[]>
}) {
  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) =>
        section.columns.length === 0 ? null : (
          <FeedSection
            key={section.id}
            title={section.title}
            count={section.level === undefined ? undefined : section.columns.length}
            dotClassName={section.level === undefined ? undefined : sectionDotClass(section.level)}
            hint={section.level === "off_category" ? OFF_CATEGORY_HINT : undefined}
          >
            {section.columns.map((column) => (
              <FeedValue
                key={column.code}
                column={column}
                value={row.values[column.code] ?? ""}
                attribute={attributesByCode.get(column.code)}
                requirement={requirementByCode?.[column.code]}
                schemaLoaded={Boolean(requirementByCode)}
                hideRequirement={section.level !== undefined}
                variantAttributes={variantAttributes.get(column.code)}
              />
            ))}
          </FeedSection>
        )
      )}
    </div>
  )
}

/** Quiet labelled section: a small header over a hairline-separated stack of
 *  attribute rows. Requirement sections carry their level's dot + a row count
 *  so coverage is scannable from the headers alone. */
function FeedSection({
  title,
  count,
  dotClassName,
  hint,
  children,
}: {
  title: string
  count?: number | undefined
  dotClassName?: string | undefined
  hint?: string | undefined
  children: ReactNode
}) {
  return (
    <section className="flex flex-col">
      <h3
        title={hint}
        className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
      >
        {dotClassName && <span className={cn("size-1.5 rounded-full", dotClassName)} />}

        {title}

        {count !== undefined && <span className="text-muted-foreground/50">{count}</span>}
      </h3>

      <div className="flex flex-col divide-y divide-border/40">{children}</div>
    </section>
  )
}

/** Product-detail header: large title, an image carousel with thumbnail tiles,
 *  the short description alongside brand + category, and the long description
 *  below — so a reviewer reads the listing the way the marketplace shopper will,
 *  while keeping the source/char-budget signals each copy field needs. */
function ProductHero({
  configSku,
  simpleSku,
  hero,
  brand,
  category,
  images,
  columns,
  requirementByCode,
  values,
}: {
  configSku: string
  simpleSku: string
  hero: HeroFields
  brand: string | null
  category: string | null
  images: readonly { url: string }[]
  columns: readonly RunFeedPreviewColumn[]
  requirementByCode: Record<string, RequirementLevel> | undefined
  values: Record<string, string>
}) {
  const trimmedTitle = hero.title?.value.trim() ?? ""
  const titleText = trimmedTitle.length > 0 ? trimmedTitle : configSku
  const hasImages = images.length > 0
  const info = (
    <ProductInfoColumn brand={brand} category={category} shortDescription={hero.shortDescription} />
  )
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <RequirementCoverage
          requirementByCode={requirementByCode}
          values={values}
          columns={columns}
        />

        <h2 className="text-lg font-semibold leading-tight text-foreground">{titleText}</h2>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span className="font-mono">{configSku}</span>

          <Link
            to="/products/$sku"
            params={{ sku: configSku }}
            className="inline-flex items-center gap-1 text-info hover:underline"
          >
            Product values
            <ExternalLink className="size-3" />
          </Link>

          {simpleSku && (
            <>
              <span className="text-muted-foreground/30">&middot;</span>

              <span className="font-mono">{simpleSku}</span>
            </>
          )}

          {hero.title?.attribute && (
            <>
              <span className="text-muted-foreground/30">&middot;</span>

              <SourceBadge source={hero.title.attribute.source} />

              <CharCount
                count={hero.title.value.length}
                max={hero.title.attribute.maxChars ?? null}
              />
            </>
          )}
        </div>
      </header>

      {hasImages ? (
        <div className="grid gap-5 sm:grid-cols-[1.5fr_minmax(0,1fr)]">
          <ProductGallery images={images} />

          {info}
        </div>
      ) : (
        info
      )}

      {hero.longDescription && <HeroLongDescription field={hero.longDescription} />}
    </div>
  )
}

/** Right-hand product info: the short description (marketing catchline) up top,
 *  brand + category as a small spec list beneath. Renders nothing when the row
 *  has none of the three. */
function ProductInfoColumn({
  brand,
  category,
  shortDescription,
}: {
  brand: string | null
  category: string | null
  shortDescription: HeroField | null
}) {
  const hasSpec = Boolean(brand) || Boolean(category)
  if (!shortDescription && !hasSpec) {
    return null
  }
  return (
    <aside className="flex flex-col gap-4">
      {shortDescription && (
        <div className="text-sm leading-relaxed text-foreground">
          <FeedValueBody
            value={shortDescription.value}
            valueLabel={undefined}
            attribute={shortDescription.attribute}
          />
        </div>
      )}

      {hasSpec && (
        <dl
          className={cn(
            "flex flex-col gap-2.5 text-xs",
            shortDescription && "border-t border-border/40 pt-4"
          )}
        >
          {brand && <SpecRow label="Brand">{brand}</SpecRow>}

          {category && <SpecRow label="Category">{category}</SpecRow>}
        </dl>
      )}
    </aside>
  )
}

function SpecRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>

      <dd className="break-words text-foreground">{children}</dd>
    </div>
  )
}

/** Long description rendered as it ships (markdown/HTML), under a quiet header. */
function HeroLongDescription({ field }: { field: HeroField }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-xs font-medium text-muted-foreground">Description</h3>

      <div className="text-sm leading-6">
        <FeedValueBody value={field.value} valueLabel={undefined} attribute={field.attribute} />
      </div>
    </section>
  )
}

/** Carousel: one large image with prev/next + an open-original control, and a
 *  4-up grid of thumbnail tiles that select the main image. State resets per
 *  product because the hero is keyed by SKU. */
function ProductGallery({ images }: { images: readonly { url: string }[] }) {
  const [active, setActive] = useState(0)
  const safeActive = Math.min(active, images.length - 1)
  const current = images[safeActive]
  const many = images.length > 1
  const step = (delta: number) => setActive((i) => (i + delta + images.length) % images.length)

  if (!current) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="group relative aspect-square w-full overflow-hidden rounded-lg border border-border/60 bg-muted">
        <img
          src={current.url}
          alt={`Product view ${safeActive + 1}`}
          loading="lazy"
          className="size-full object-contain"
        />

        <a
          href={current.url}
          target="_blank"
          rel="noreferrer"
          title="Open full image"
          className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-md bg-background/80 text-muted-foreground opacity-0 backdrop-blur transition-opacity hover:text-foreground group-hover:opacity-100"
        >
          <ExternalLink className="size-3.5" />
        </a>

        {many && (
          <>
            <GalleryArrow side="left" onClick={() => step(-1)} />

            <GalleryArrow side="right" onClick={() => step(1)} />

            <div className="absolute bottom-2 right-2 rounded-full bg-background/80 px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground backdrop-blur">
              {`${safeActive + 1} / ${images.length}`}
            </div>
          </>
        )}
      </div>

      {many && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              type="button"
              onClick={() => setActive(index)}
              title={`Image ${index + 1}`}
              className={cn(
                "aspect-square overflow-hidden rounded-md border bg-muted transition-colors",
                index === safeActive
                  ? "border-foreground ring-1 ring-foreground"
                  : "border-border/60 hover:border-border"
              )}
            >
              <img
                src={image.url}
                alt={`Thumbnail ${index + 1}`}
                loading="lazy"
                className="size-full object-contain"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function GalleryArrow({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Previous image" : "Next image"}
      className={cn(
        "absolute top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground opacity-0 shadow-sm backdrop-blur transition-opacity hover:bg-background group-hover:opacity-100",
        side === "left" ? "left-2" : "right-2"
      )}
    >
      <Icon className="size-4" />
    </button>
  )
}

function DrawerToolbar({
  previousRow,
  nextRow,
  onClose,
  onNavigateRow,
}: {
  previousRow: RunFeedPreviewRow | null
  nextRow: RunFeedPreviewRow | null
  onClose: () => void
  onNavigateRow: (row: RunFeedPreviewRow) => void
}) {
  return (
    <div className="sticky top-0 z-20 flex items-center justify-end gap-1 border-b border-border bg-background/95 p-2 backdrop-blur">
      <button
        type="button"
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
        aria-label="Previous config SKU"
        title="Previous config SKU"
        disabled={!previousRow}
        onClick={() => {
          if (previousRow) {
            onNavigateRow(previousRow)
          }
        }}
      >
        <ChevronUp className="size-4" />
      </button>

      <button
        type="button"
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
        aria-label="Next config SKU"
        title="Next config SKU"
        disabled={!nextRow}
        onClick={() => {
          if (nextRow) {
            onNavigateRow(nextRow)
          }
        }}
      >
        <ChevronDown className="size-4" />
      </button>

      <button
        type="button"
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Close"
        title="Close"
        onClick={onClose}
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

function FeedValue({
  column,
  value,
  attribute,
  requirement,
  schemaLoaded,
  hideRequirement,
  variantAttributes,
}: {
  column: RunFeedPreviewColumn
  value: string
  attribute: RunSkuPreviewAttribute | undefined
  requirement: RequirementLevel | undefined
  schemaLoaded: boolean
  /** True inside a requirement-grouped section, where the section header
   *  already states the level — the per-row chip would just repeat it. */
  hideRequirement: boolean
  variantAttributes?: readonly RunSkuPreviewAttribute[] | undefined
}) {
  // A column with no requirement once the schema has loaded isn't an attribute
  // of this SKU's category — it belongs to a sibling category batched into the
  // same run. Mark it "n/a" so its empty value reads as intentional, not broken.
  const offCategory = schemaLoaded && !requirement
  const showRequirement = !hideRequirement && (Boolean(requirement) || offCategory)
  const hasMeta = showRequirement || Boolean(attribute)
  return (
    <div className="grid gap-x-4 gap-y-1 py-2.5 sm:grid-cols-[13rem_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="break-words text-xs font-medium text-foreground">{column.label}</span>

        {/* Fixed metadata row: requirement (or n/a) first, source second — same
            place for every attribute so the eye can scan one column of chips. */}
        {hasMeta && (
          <div className="flex flex-wrap items-center gap-1.5">
            {showRequirement && (
              <RequirementSlot requirement={requirement} offCategory={offCategory} />
            )}

            {attribute && <SourceBadge source={attribute.source} />}
          </div>
        )}

        <span className="break-all font-mono text-xs text-muted-foreground/70">{column.code}</span>
      </div>

      <div className="max-h-64 min-w-0 overflow-auto break-words text-sm leading-6">
        {variantAttributes ? (
          <VariantAttributeValues attributes={variantAttributes} column={column} />
        ) : (
          <FeedValueBody
            value={value}
            valueLabel={column.valueLabels?.[value]}
            attribute={attribute}
          />
        )}
      </div>
    </div>
  )
}

function VariantAttributeValues({
  attributes,
  column,
}: {
  attributes: readonly RunSkuPreviewAttribute[]
  column: RunFeedPreviewColumn
}) {
  return (
    <details>
      <summary className="cursor-pointer text-sm text-foreground">
        Per variant · {attributes.length} inspected
      </summary>

      <dl className="mt-2 flex flex-col gap-2">
        {attributes.map((attribute) => (
          <div key={attribute.simpleSku}>
            <dt className="break-all font-mono text-xs text-muted-foreground">
              {attribute.simpleSku}
            </dt>

            <dd>
              <FeedValueBody
                value={attribute.rewrittenValue ?? ""}
                valueLabel={column.valueLabels?.[attribute.rewrittenValue ?? ""]}
                attribute={attribute}
              />
            </dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

/** The requirement chip: the attribute's level, or "n/a" once we know it isn't
 *  part of this SKU's category. Nothing while the schema is still loading. */
function RequirementSlot({
  requirement,
  offCategory,
}: {
  requirement: RequirementLevel | undefined
  offCategory: boolean
}) {
  if (requirement) {
    return <RequirementBadge requirement={requirement} />
  }
  if (offCategory) {
    return <OffCategoryBadge />
  }
  return null
}

function FeedValueBody({
  value,
  valueLabel,
  attribute,
}: {
  value: string
  valueLabel: string | undefined
  attribute: RunSkuPreviewAttribute | undefined
}) {
  if (attribute?.gap) {
    return (
      <span className="text-warning">
        <code>{attribute.gap.reason}</code>

        {`: ${attribute.gap.message}`}
      </span>
    )
  }

  // Value-list / hierarchy codes: show the human label with the raw code beneath
  // so the operator sees both what ships and what it means.
  if (valueLabel) {
    return (
      <div className="space-y-0.5">
        <div className="text-foreground">{valueLabel}</div>

        <div className="font-mono text-xs text-muted-foreground/70">{value}</div>
      </div>
    )
  }

  return <ResolvedValue value={value} attribute={attribute} />
}

/** Empty cell: clearly "no value" rather than a literal that competes with real
 *  data. Muted + italic so populated rows visually win. */
function EmptyValue() {
  return <span className="italic text-muted-foreground/50">empty</span>
}

function ResolvedValue({
  value,
  attribute,
}: {
  value: string
  attribute: RunSkuPreviewAttribute | undefined
}) {
  const format = attribute?.format
  const isRichText = format === "markdown" || format === "html"
  const showCharCount = Boolean(attribute && AI_SOURCES.has(attribute.source))

  const renderValue = () => {
    if (isRichText && value) {
      return <RichTextValue value={value} format={format} />
    }
    if (!value) {
      return <EmptyValue />
    }
    return <div className="whitespace-pre-wrap break-words text-foreground">{value}</div>
  }

  return (
    <div className="space-y-1">
      {renderValue()}

      {showCharCount && <CharCount count={value.length} max={attribute?.maxChars ?? null} />}

      {attribute && attribute.rewritesApplied.length > 0 && (
        <div className="text-xs text-muted-foreground/70">
          rewrites: {attribute.rewritesApplied.join(", ")}
        </div>
      )}
    </div>
  )
}

/** Char budget for AI-authored fields. Counts UTF-16 code units to match the
 *  writer's `maxChars` check; turns rose once the declared limit is exceeded. */
function CharCount({ count, max }: { count: number; max: number | null }) {
  const over = max !== null && count > max
  const label =
    max !== null
      ? `${count.toLocaleString()} / ${max.toLocaleString()} characters`
      : `${count.toLocaleString()} characters`
  return (
    <div className={cn("text-xs", over ? "text-destructive" : "text-muted-foreground")}>
      {label}
    </div>
  )
}
